import { supabase } from '../lib/supabase'

interface AutoPopulationResult {
  success: boolean
  facilitiesCreated: number
  venuesCreated: number
  error?: string
}

interface FacilityLimit {
  currentCount: number
  maxAllowed: number | null
  canAddMore: boolean
  isAtLimit: boolean
  planCode: string
}

export async function autoPopulateFacilities(
  organizationId: string,
  subscriptionTier: string
): Promise<AutoPopulationResult> {
  try {
    // Call the database function to auto-populate
    const { data, error } = await supabase
      .rpc('auto_populate_facilities', {
        org_id: organizationId,
        tier_code: subscriptionTier
      })
      .single()
    
    if (error) throw error
    
    return {
      success: true,
      facilitiesCreated: data.facilities_created || 0,
      venuesCreated: data.venues_created || 0
    }
  } catch (error: unknown) {
    console.error('Error auto-populating facilities:', error)
    return {
      success: false,
      facilitiesCreated: 0,
      venuesCreated: 0,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

// Check if organization can add more facilities
export async function checkFacilityLimit(organizationId: string): Promise<FacilityLimit> {
  try {
    const { data, error } = await supabase
      .rpc('check_facility_limit', { org_id: organizationId })
      .single()
    
    if (error) {
      console.error('Error checking facility limit:', error)
      return {
        currentCount: 0,
        maxAllowed: null,
        canAddMore: true,
        isAtLimit: false,
        planCode: ''
      }
    }
    
    return {
      currentCount: data.current_count || 0,
      maxAllowed: data.max_allowed,
      canAddMore: data.can_add_more || false,
      isAtLimit: data.is_at_limit || false,
      planCode: data.plan_code || ''
    }
  } catch (error) {
    console.error('Error in checkFacilityLimit:', error)
    return {
      currentCount: 0,
      maxAllowed: null,
      canAddMore: true,
      isAtLimit: false,
      planCode: ''
    }
  }
}

// Get subscription plan details for an organization
export async function getOrganizationPlan(organizationId: string) {
  try {
    const { data, error } = await supabase
      .from('organization_subscriptions')
      .select(`
        *,
        subscription_plans (*)
      `)
      .eq('organization_id', organizationId)
      .in('status', ['active', 'trial'])
      .single()
    
    if (error) throw error
    
    return data
  } catch (error) {
    console.error('Error getting organization plan:', error)
    return null
  }
}

// Migrate existing organizations (admin function)
export async function migrateExistingOrganizations() {
  try {
    const { data, error } = await supabase
      .rpc('migrate_existing_organizations')
    
    if (error) throw error
    
    return {
      success: true,
      results: data
    }
  } catch (error: unknown) {
    console.error('Error migrating organizations:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      results: []
    }
  }
}

// Check if organization needs facility setup
export async function checkNeedsFacilitySetup(organizationId: string): Promise<boolean> {
  try {
    // Check facility limit which includes current count
    const limit = await checkFacilityLimit(organizationId)
    
    // If they have no facilities/venues and a valid plan, they need setup
    return limit.currentCount === 0 && limit.planCode !== ''
  } catch (error) {
    console.error('Error checking facility setup need:', error)
    return false
  }
}